/**
 * Camera reframe for non-full drill pitch views. When the drill canvas
 * is in half / quarter / custom view, the standard 'tactical' preset
 * (parked for a 105×68m pitch) puts the working area in the distance.
 * This component moves the camera + OrbitControls target to fit the
 * visible region the moment 3D mounts.
 *
 * Runs once on mount + whenever the framed region changes (pitch
 * spec / view / custom dims). Doesn't fight OrbitControls — coach can
 * pan/zoom after the initial reframe.
 */
import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { visibleRegion, type PitchView, type CustomPitch } from '../pitchArea';
import type { PitchSpec } from '../pitchSpecs';

interface Props {
  spec: PitchSpec;
  view: PitchView;
  custom: CustomPitch | null;
}

export function CameraReframer({ spec, view, custom }: Props): null {
  // Pull the controls ref out of R3F state (drei's OrbitControls sets it).
  const r3f = useThree();
  const camera = r3f.camera;
  const controls = (r3f as unknown as { controls?: { target: { set: (x: number, y: number, z: number) => void }; update: () => void } }).controls;

  useEffect(() => {
    if (view === 'full') return;

    const region = visibleRegion(spec, view, custom);
    const targetX = region.cy;
    const targetZ = region.cx;
    // Tighter framing for small grids — coach should see the working
    // area filling most of the frame. Scales linearly with the longest
    // extent + a generous angular fit assumption (FOV ~32°).
    const fitDim = Math.max(region.length, region.width);
    const distance = Math.max(12, fitDim * 1.05);
    const height = Math.max(10, fitDim * 0.65);

    const apply = () => {
      camera.position.set(targetX, height, targetZ + distance);
      camera.lookAt(targetX, 0, targetZ);
      camera.updateProjectionMatrix();
      if (controls) {
        controls.target.set(targetX, 0, targetZ);
        controls.update();
      }
    };

    // Apply once immediately, then again after CameraRig's preset
    // tween completes (~700ms) so the tactical preset doesn't
    // overwrite the reframe. The second application also re-targets
    // OrbitControls so user pan/zoom centres on the working area.
    apply();
    const tween1 = setTimeout(apply, 200);
    const tween2 = setTimeout(apply, 800);
    return () => {
      clearTimeout(tween1);
      clearTimeout(tween2);
    };
  }, [camera, controls, spec, view, custom]);

  return null;
}
